const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

class PackBuilder extends HTMLElement {
  #cart = new Map();
  #currentStep = 1;
  #totalSteps = 1;
  #maxItems = 0; // 0 = illimité
  #moneyFormatter;
  #storageKey;

  connectedCallback() {
    this.#totalSteps = this.querySelectorAll('.pack-builder__step').length;
    this.#maxItems = Number(this.dataset.maxItems) || 0;
    this.#storageKey = `pack-builder-${this.dataset.sectionId || 'default'}-state`;
    this.#initFormatter();
    this.#restoreState();
    this.#bindNav();
    this.#bindCards();
    this.#bindClear();
    this.#bindCheckout();
    this.#bindMobileCta();
    this.#bindNextStep();
    this.#updateUI();
  }

  #initFormatter() {
    const locale = this.dataset.locale || 'fr-FR';
    const currency = this.dataset.currency || 'EUR';
    try {
      this.#moneyFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (e) {
      this.#moneyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
    }
  }

  #formatMoney(cents) {
    return this.#moneyFormatter.format((cents || 0) / 100);
  }

  // ===== Navigation =====
  #bindNav() {
    this.querySelectorAll('.pack-builder__tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const step = Number(btn.dataset.step);
        if (!step) return;
        this.#goToStep(step);
      });
    });
  }

  #bindNextStep() {
    this.querySelectorAll('.pack-builder__next-step').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = Number(btn.dataset.nextStep);
        if (!next) return;
        this.#goToStep(next);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  #goToStep(n) {
    this.#currentStep = n;
    this.querySelectorAll('.pack-builder__tab').forEach((btn) => {
      const isActive = Number(btn.dataset.step) === n;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.querySelectorAll('.pack-builder__step').forEach((s) => {
      s.hidden = Number(s.dataset.step) !== n;
    });
  }

  // ===== Cards =====
  #bindCards() {
    this.querySelectorAll('.pack-builder__card').forEach((card) => {
      const select = card.querySelector('.pack-builder__variant-select');
      const addBtn = card.querySelector('.pack-builder__add');
      const minusBtn = card.querySelector('[data-action="minus"]');
      const plusBtn = card.querySelector('[data-action="plus"]');
      const priceDisplay = card.querySelector('[data-price-display]');

      if (select && addBtn) {
        select.addEventListener('change', () => {
          const opt = select.options[select.selectedIndex];
          if (!opt) return;
          addBtn.dataset.variantId = opt.value;
          addBtn.dataset.price = opt.dataset.price || '';
          addBtn.dataset.formattedPrice = opt.dataset.formattedPrice || '';
          if (opt.dataset.image) addBtn.dataset.image = opt.dataset.image;
          if (opt.dataset.title) addBtn.dataset.title = opt.dataset.title;
          addBtn.dataset.inventoryMax = opt.dataset.inventoryMax || '';
          addBtn.dataset.qtyMin = opt.dataset.qtyMin || '1';
          addBtn.dataset.qtyStep = opt.dataset.qtyStep || '1';
          addBtn.dataset.qtyMax = opt.dataset.qtyMax || '';
          const available = opt.dataset.available === 'true';
          addBtn.disabled = !available;
          card.classList.toggle('is-sold-out', !available);
          if (priceDisplay) {
            const formattedCompare = opt.dataset.formattedCompare || '';
            const formattedPrice = opt.dataset.formattedPrice || '';
            if (formattedCompare) {
              priceDisplay.innerHTML = `<span class="pack-builder__price-current">${formattedPrice}</span> <span class="pack-builder__price-compare">${formattedCompare}</span>`;
            } else {
              priceDisplay.textContent = formattedPrice;
            }
          }
        });
      }

      addBtn?.addEventListener('click', () => {
        if (addBtn.disabled) return;
        const id = addBtn.dataset.variantId;
        if (!id) return;

        // Limite max d'items dans le pack
        if (this.#maxItems > 0 && !this.#cart.has(id) && this.#cart.size >= this.#maxItems) {
          this.#showError(`Limite atteinte : ${this.#maxItems} produits maximum dans le pack.`);
          return;
        }

        const qmin = Number(addBtn.dataset.qtyMin) || 1;
        const data = {
          variantId: id,
          title: addBtn.dataset.title || '',
          price: Number(addBtn.dataset.price) || 0,
          image: addBtn.dataset.image || '',
          quantity: qmin,
          qtyMin: qmin,
          qtyStep: Number(addBtn.dataset.qtyStep) || 1,
          qtyMax: Number(addBtn.dataset.qtyMax) || 0,
          inventoryMax: Number(addBtn.dataset.inventoryMax) || 0,
        };
        this.#cart.set(id, data);
        card.dataset.selectedVariant = id;
        this.#syncCardUI(card, true);
        this.#hideError();
        this.#saveState();
        this.#updateUI();
      });

      minusBtn?.addEventListener('click', () => {
        const id = card.dataset.selectedVariant;
        if (!id) return;
        const item = this.#cart.get(id);
        if (!item) return;
        const newQty = item.quantity - (item.qtyStep || 1);
        if (newQty < (item.qtyMin || 1)) {
          this.#cart.delete(id);
          delete card.dataset.selectedVariant;
          this.#syncCardUI(card, false);
        } else {
          item.quantity = newQty;
          this.#cart.set(id, item);
          this.#syncCardUI(card, true);
        }
        this.#hideError();
        this.#saveState();
        this.#updateUI();
      });

      plusBtn?.addEventListener('click', () => {
        const id = card.dataset.selectedVariant;
        if (!id) return;
        const item = this.#cart.get(id);
        if (!item) return;
        const step = item.qtyStep || 1;
        const newQty = item.quantity + step;
        // Respect quantity_rule.max si défini
        if (item.qtyMax && newQty > item.qtyMax) {
          this.#showError(`Maximum ${item.qtyMax} pour ce produit.`);
          return;
        }
        // Respect inventaire si tracking
        if (item.inventoryMax && newQty > item.inventoryMax) {
          this.#showError(`Stock insuffisant (${item.inventoryMax} dispo).`);
          return;
        }
        item.quantity = newQty;
        this.#cart.set(id, item);
        this.#syncCardUI(card, true);
        this.#hideError();
        this.#saveState();
        this.#updateUI();
      });
    });
  }

  #syncCardUI(card, selected) {
    const addBtn = card.querySelector('.pack-builder__add');
    const qtyWrap = card.querySelector('.pack-builder__qty');
    const qtyValue = card.querySelector('.pack-builder__qty-value');
    if (!addBtn || !qtyWrap) return;

    if (selected) {
      const id = card.dataset.selectedVariant;
      const item = id ? this.#cart.get(id) : null;
      addBtn.hidden = true;
      qtyWrap.hidden = false;
      if (qtyValue && item) qtyValue.textContent = String(item.quantity);
      card.classList.add('is-selected');
    } else {
      addBtn.hidden = false;
      qtyWrap.hidden = true;
      if (qtyValue) qtyValue.textContent = '1';
      card.classList.remove('is-selected');
    }
  }

  // ===== Clear =====
  #bindClear() {
    const clearBtn = this.querySelector('.pack-builder__clear');
    clearBtn?.addEventListener('click', () => {
      if (!confirm('Vider votre routine ?')) return;
      this.#clearAll();
    });
  }

  #clearAll() {
    this.#cart.clear();
    this.querySelectorAll('.pack-builder__card.is-selected').forEach((card) => {
      delete card.dataset.selectedVariant;
      this.#syncCardUI(card, false);
    });
    this.#hideError();
    this.#saveState();
    this.#updateUI();
  }

  // ===== Errors =====
  #showError(msg) {
    const el = this.querySelector('.pack-builder__error');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  #hideError() {
    const el = this.querySelector('.pack-builder__error');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  // ===== Checkout =====
  #bindCheckout() {
    const btn = this.querySelector('.pack-builder__checkout');
    btn?.addEventListener('click', () => this.#checkout(btn));
  }

  #bindMobileCta() {
    const btn = this.querySelector('.pack-builder__mobile-checkout');
    btn?.addEventListener('click', () => {
      const desktopBtn = this.querySelector('.pack-builder__checkout');
      this.#checkout(desktopBtn || btn);
    });
  }

  async #checkout(btn) {
    if (this.#cart.size === 0) return;
    this.#hideError();
    btn.disabled = true;
    btn.dataset.loading = 'true';
    btn.setAttribute('aria-busy', 'true');

    const items = Array.from(this.#cart.values()).map((item) => ({
      id: Number(item.variantId),
      quantity: item.quantity,
    }));

    try {
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { source: 'pack-builder' } }),
      });

      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || "Erreur lors de l'ajout au panier");
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }

      this.#clearAll();
    } catch (err) {
      this.#showError(err.message || 'Erreur');
    } finally {
      btn.disabled = this.#cart.size === 0;
      delete btn.dataset.loading;
      btn.removeAttribute('aria-busy');
    }
  }

  // ===== UI Update =====
  #updateUI() {
    const slotsContainer = this.querySelector('.pack-builder__slots');
    const checkoutBtn = this.querySelector('.pack-builder__checkout');
    const checkoutTotal = this.querySelector('.pack-builder__checkout-total');
    const clearBtn = this.querySelector('.pack-builder__clear');
    const mobileCta = this.querySelector('.pack-builder__mobile-cta');
    const mobileCount = this.querySelector('.pack-builder__mobile-count');
    const mobileTotal = this.querySelector('.pack-builder__mobile-total');
    const completedEl = this.querySelector('[data-completed-steps]');

    let totalCents = 0;
    let totalQty = 0;

    // Compteur étapes complétées + badges par tab
    const stepsWithItems = new Set();
    this.querySelectorAll('.pack-builder__step').forEach((stepEl) => {
      const stepNum = Number(stepEl.dataset.step);
      const hasSelected = stepEl.querySelector('.pack-builder__card.is-selected');
      if (hasSelected) stepsWithItems.add(stepNum);

      // Badge de tab : nb produits sélectionnés dans cette étape
      const stepTab = this.querySelector(`.pack-builder__tab[data-step="${stepNum}"]`);
      const badge = stepTab?.querySelector('[data-step-badge]');
      if (badge) {
        const count = stepEl.querySelectorAll('.pack-builder__card.is-selected').length;
        if (count > 0) {
          badge.textContent = String(count);
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }
    });
    if (completedEl) completedEl.textContent = String(stepsWithItems.size);

    // Slots récap
    if (slotsContainer) {
      if (this.#cart.size === 0) {
        const emptyText = slotsContainer.dataset.emptyText || '';
        slotsContainer.innerHTML = `<p class="pack-builder__slots-empty">${emptyText}</p>`;
      } else {
        const html = Array.from(this.#cart.values())
          .map((item) => {
            totalCents += item.price * item.quantity;
            totalQty += item.quantity;
            const qtyBadge = item.quantity > 1 ? `<span class="pack-builder__slot-qty">${item.quantity}</span>` : '';
            const img = item.image
              ? `<img src="${item.image}" alt="${this.#escape(item.title)}" loading="lazy">`
              : '<div class="pack-builder__slot-placeholder"></div>';
            return `
              <div class="pack-builder__slot">
                ${img}
                ${qtyBadge}
                <button type="button" class="pack-builder__slot-remove" data-id="${item.variantId}" aria-label="Retirer">×</button>
                <div class="pack-builder__slot-info">
                  <p class="pack-builder__slot-title">${this.#escape(item.title)}</p>
                  <p class="pack-builder__slot-price">${this.#formatMoney(item.price * item.quantity)}</p>
                </div>
              </div>
            `;
          })
          .join('');
        slotsContainer.innerHTML = html;
        slotsContainer.querySelectorAll('.pack-builder__slot-remove').forEach((btn) => {
          btn.addEventListener('click', () => {
            const id = btn.dataset.id;
            if (!id) return;
            this.#cart.delete(id);
            const card = this.querySelector(`.pack-builder__card[data-selected-variant="${id}"]`);
            if (card) {
              delete card.dataset.selectedVariant;
              this.#syncCardUI(card, false);
            }
            this.#hideError();
            this.#saveState();
            this.#updateUI();
          });
        });
      }
    } else {
      Array.from(this.#cart.values()).forEach((i) => {
        totalCents += i.price * i.quantity;
        totalQty += i.quantity;
      });
    }

    if (checkoutBtn) checkoutBtn.disabled = this.#cart.size === 0;
    if (checkoutTotal) checkoutTotal.textContent = this.#formatMoney(totalCents);
    if (clearBtn) clearBtn.hidden = this.#cart.size === 0;

    if (mobileCta) {
      mobileCta.hidden = this.#cart.size === 0;
      if (mobileCount) mobileCount.textContent = String(totalQty);
      if (mobileTotal) mobileTotal.textContent = this.#formatMoney(totalCents);
    }
  }

  #escape(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ===== Persistence (par section, expire après 7j) =====
  #saveState() {
    try {
      if (this.#cart.size === 0) {
        localStorage.removeItem(this.#storageKey);
        return;
      }
      const payload = {
        timestamp: Date.now(),
        items: Array.from(this.#cart.values()),
      };
      localStorage.setItem(this.#storageKey, JSON.stringify(payload));
    } catch (e) {}
  }

  #restoreState() {
    try {
      const raw = localStorage.getItem(this.#storageKey);
      if (!raw) return;
      const payload = JSON.parse(raw);
      // Compatibilité ascendante (ancien format = tableau direct)
      const items = Array.isArray(payload) ? payload : payload.items;
      const ts = Array.isArray(payload) ? 0 : payload.timestamp || 0;

      // Expiration
      if (ts && Date.now() - ts > STORAGE_TTL_MS) {
        localStorage.removeItem(this.#storageKey);
        return;
      }
      if (!Array.isArray(items)) return;

      items.forEach((item) => {
        if (!item || !item.variantId) return;
        const id = String(item.variantId);
        this.#cart.set(id, {
          variantId: id,
          title: item.title || '',
          price: Number(item.price) || 0,
          image: item.image || '',
          quantity: Number(item.quantity) || 1,
          qtyMin: Number(item.qtyMin) || 1,
          qtyStep: Number(item.qtyStep) || 1,
          qtyMax: Number(item.qtyMax) || 0,
          inventoryMax: Number(item.inventoryMax) || 0,
        });
        const addBtn = this.querySelector(`.pack-builder__card .pack-builder__add[data-variant-id="${id}"]`);
        const card = addBtn?.closest('.pack-builder__card');
        if (card) {
          card.dataset.selectedVariant = id;
          this.#syncCardUI(card, true);
        }
      });
    } catch (e) {}
  }
}

if (!customElements.get('pack-builder')) {
  customElements.define('pack-builder', PackBuilder);
}
